import { MobileSelect, type SelectOption } from '@/components/ui';
import type { Trip } from '../types';

interface TripCardProps {
  trip: Trip;
  variant?: 'default' | 'compact';
  // Assignment props
  showAssignment?: boolean;
  projectOptions?: SelectOption[];
  selectedProjectId?: string;
  onProjectChange?: (projectId: string) => void;
  onAssign?: () => void;
  onDismiss?: () => void;
  isProcessing?: boolean;
}

export function TripCard({
  trip,
  variant = 'default',
  showAssignment = false,
  projectOptions = [],
  selectedProjectId = '',
  onProjectChange,
  onAssign,
  onDismiss,
  isProcessing = false,
}: TripCardProps) {
  const confidenceColors = {
    high: 'bg-green-100 text-green-700 border-green-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-orange-100 text-orange-700 border-orange-200',
    none: 'bg-secondary-100 text-secondary-700 border-secondary-200',
  };

  const statusColors = {
    pending_review: 'bg-yellow-100 text-yellow-700',
    auto_assigned: 'bg-green-100 text-green-700',
    manually_assigned: 'bg-blue-100 text-blue-700',
    unassigned: 'bg-secondary-100 text-secondary-700',
  };

  const statusLabels = {
    pending_review: 'Pending Review',
    auto_assigned: 'Auto Assigned',
    manually_assigned: 'Manually Assigned',
    unassigned: 'Unassigned',
  };

  const formatAddress = (address: string | null): string => {
    if (!address) return 'Unknown location';
    if (address.length > 40) {
      return address.substring(0, 37) + '...';
    }
    return address;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-CA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-CA', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDistance = (km: number | null): string => {
    if (km === null || km === undefined) return '— km';
    return `${km.toFixed(1)} km`;
  };

  if (variant === 'compact') {
    return (
      <div className="bg-white border border-secondary-200 rounded-lg p-3">
        {/* Compact header */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-secondary-900">
            {formatDate(trip.startTime)}
          </span>
          <span className="text-sm text-secondary-600">
            {formatDistance(trip.distanceKm)}
          </span>
        </div>

        {/* Route display - compact */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-secondary-600 truncate flex-1" title={trip.startAddressText || undefined}>
            {formatAddress(trip.startAddressText)}
          </span>
          <svg className="w-4 h-4 text-secondary-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
          <span className="text-secondary-600 truncate flex-1" title={trip.endAddressText || undefined}>
            {formatAddress(trip.endAddressText)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-secondary-200 rounded-lg">
      {/* Card Header */}
      <div className="px-4 py-3 bg-secondary-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-secondary-900">
              {formatDate(trip.startTime)}
            </span>
            <span className="text-sm text-secondary-500">
              {formatTime(trip.startTime)}
            </span>
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[trip.assignmentStatus]}`}>
            {statusLabels[trip.assignmentStatus]}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-4">
        {/* Route Display */}
        <div className="space-y-3">
          {/* Start Location */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">Start</p>
              <p className="text-sm text-secondary-900 break-words" title={trip.startAddressText || undefined}>
                {trip.startAddressText || 'Unknown location'}
              </p>
            </div>
          </div>

          {/* Connecting Line */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 flex justify-center">
              <div className="w-0.5 h-6 bg-secondary-300" />
            </div>
            <div className="flex-1 flex items-center gap-2 text-sm text-secondary-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <span>{formatDistance(trip.distanceKm)}</span>
            </div>
          </div>

          {/* End Location */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-secondary-500 uppercase tracking-wide">End</p>
              <p className="text-sm text-secondary-900 break-words" title={trip.endAddressText || undefined}>
                {trip.endAddressText || 'Unknown location'}
              </p>
            </div>
          </div>
        </div>

        {/* Trip Details */}        
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2">
          {trip.driverName && (
            <div className="flex items-center gap-1.5 text-sm text-secondary-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{trip.driverName}</span>
            </div>
          )}
          {!trip.billable && (
            <span className="px-2 py-0.5 text-xs font-medium bg-secondary-100 text-secondary-600 rounded">
              Non-billable
            </span>
          )}
        </div>

        {/* Assigned Project */}
        {trip.projectName && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="text-sm font-medium text-blue-900">{trip.projectName}</span>
            {trip.customerName && (
              <span className="text-sm text-blue-700">- {trip.customerName}</span>
            )}
          </div>
        )}

        {/* Suggested Project (when pending) */}
        {showAssignment && trip.suggestedProjectName && (
          <div className="p-3 bg-secondary-50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-secondary-500 uppercase tracking-wide">
                Suggested Match
              </span>
              {trip.matchConfidence && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${confidenceColors[trip.matchConfidence]}`}>
                  {trip.matchConfidence} confidence
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-secondary-900">
              {trip.suggestedProjectName}
            </p>
            {trip.suggestedCustomerName && (
              <p className="text-sm text-secondary-600">{trip.suggestedCustomerName}</p>
            )}
            {trip.matchDistanceM !== null && (
              <p className="text-xs text-secondary-500">
                {Math.round(trip.matchDistanceM)}m from {trip.matchLocation === 'end' ? 'destination' : 'start'}
              </p>
            )}
          </div>
        )}

        {/* Assignment Controls */}
        {showAssignment && onProjectChange && (
          <div className="pt-2 space-y-4">
            <MobileSelect
              label="Assign to Project"
              value={selectedProjectId}
              onChange={onProjectChange}
              disabled={isProcessing}
              placeholder="Select a project..."
              options={projectOptions}
            />

            <div className="flex gap-2">
              <button
                onClick={onAssign}
                disabled={!selectedProjectId || isProcessing}
                className="flex-1 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? 'Saving...' : 'Assign to Project'}
              </button>

              <button
                onClick={onDismiss}
                disabled={isProcessing}
                className="px-4 py-2.5 border border-secondary-300 text-secondary-700 text-sm font-medium rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}